import React, { useEffect, useState } from 'react';

import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useLocation, useNavigate } from 'react-router-dom';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';

import SuccessModal from './components/SuccessModal';
import useRegister from './hooks/useRegister';
import Button from 'components/Button';
import {
  OnboardingContentCenter,
  OnboardingForm,
  OnboardingInputsContainer,
  OnboardingLayout,
  OnboardingNameSurnameContainer,
} from 'components/OnboardingComponents';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useDynamicLogoForm } from 'hooks/useDynamicLogoForm';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import 'react-phone-input-2/lib/style.css';
import { apiOrchestrator } from 'services/apiService';
import { useIsDesktop, useIsMobile, useIsTablet } from 'utils/mediaQueries';

export const Register: React.FC = () => {
  const theme = useThemeContext();
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [inviteeName, setInviteeName] = useState<string | null>(null);

  const { control, handleSubmit, errors, onSubmit, loading } = useRegister(setDuplicateEmailError);

  const navigate = useNavigate();
  const location = useLocation();

  const [inviteId, setInviteId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteAction, setInviteAction] = useState<'accept' | 'decline' | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showInviteRequiredModal, setShowInviteRequiredModal] = useState(false);

  const { toastSuccess, toastError } = useToast();

  const { mediaUrl, isLoading: isLogoLoading } = useDynamicLogoForm();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const invite = searchParams.get('invite_id');
    if (invite) {
      setInviteId(invite);
      setShowInviteModal(true);
    } else {
      setShowInviteRequiredModal(true);
    }
  }, [location.search]);

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleInviteResponse = async (action: 'accept' | 'decline') => {
    setInviteAction(action);
    setShowInviteModal(false);

    if (action === 'decline' && inviteId) {
      try {
        await respondToInvite(inviteId, null, 'decline');
        toastSuccess('Convite recusado com sucesso.');
      } catch (error) {
        console.error('Erro ao recusar o convite:', error);
        toastError('Erro ao recusar o convite.');
      }
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      const response = await onSubmit(data);
      if (response) {
        setShowSuccessModal(true);
        setInviteeName(`${data.firstName} ${data.lastName}`);
      }
    } catch (error) {
      console.error('Erro ao registrar:', error);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  const respondToInvite = async (
    inviteId: string,
    userId: string | null,
    action: 'accept' | 'decline'
  ) => {
    const url = `/invites/invite/${inviteId}/respond-invite?action=${action}${userId ? `&user_id=${userId}` : ''}`;
    const response = await apiOrchestrator.post(url);
    return response.data;
  };

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();

  return (
    <OnboardingLayout>
      <OnboardingForm onSubmit={handleSubmit(handleFormSubmit)}>
        <OnboardingContentCenter>
          <div className="top-2"></div>

          <OnboardingInputsContainer>
            <Text size="medium" color="textPrimary">
              Preencha os dados abaixo para se registrar
            </Text>
            <OnboardingNameSurnameContainer>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome"
                    variant="outlined"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    size="small"
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Sobrenome"
                    variant="outlined"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    size="small"
                  />
                )}
              />
            </OnboardingNameSurnameContainer>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="E-mail"
                  variant="outlined"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  size="small"
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  {...field}
                  country={'br'}
                  specialLabel=""
                  placeholder="Telefone"
                  containerStyle={{
                    width: '100%',
                  }}
                  buttonStyle={{
                    backgroundColor: theme.background.secondary,
                    border: `1px solid ${errors.phone ? theme.error : theme.text.primary}`,
                  }}
                  dropdownStyle={{
                    backgroundColor: theme.background.secondary,
                  }}
                  inputStyle={{
                    width: '100%',
                    //height: '56px',
                    backgroundColor: theme.background.secondary,
                    border: `1px solid ${errors.phone ? theme.error : theme.text.primary}`,
                    color: theme.text.primary,
                  }}
                />
              )}
            />
            {errors.phone && (
              <span
                style={{
                  color: `${theme.error}`,
                  fontSize: '10px',
                  marginLeft: '1rem',
                  marginTop: '-0.5rem',
                }}
              >
                {errors.phone.message}
              </span>
            )}

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirmar senha"
                  type={showConfirmPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={toggleShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </OnboardingInputsContainer>

          <Button type="submit" loading={loading}>
            {loading ? 'Carregando...' : 'Registrar-se'}
          </Button>
        </OnboardingContentCenter>

        <Button
          type="button"
          title="Já possui uma conta?"
          size="medium"
          variant="minimal"
          onClick={() => navigate('/login')}
        >
          Já possui uma conta?
        </Button>
      </OnboardingForm>

      <GlobalModal
        open={duplicateEmailError}
        title="ERRO DE CADASTRO"
        description="O e-mail informado já está cadastrado. Tente usar outro e-mail ou recupere sua senha."
        onClose={() => setDuplicateEmailError(false)}
        confirmButtonText="Fechar"
      />

      <GlobalModal
        open={showInviteModal}
        title="CONVITE DE REGISTRO"
        description="Você recebeu um convite para se registrar. Deseja aceitá-lo?"
        onClose={() => setShowInviteModal(false)}
        onConfirm={() => handleInviteResponse('accept')}
        confirmButtonText="Aceitar"
        cancelButtonText="Recusar"
        onCancel={() => handleInviteResponse('decline')}
      />

      <GlobalModal
        open={showInviteRequiredModal}
        title="CONVITE NECESSÁRIO"
        description="Para registrar-se, é necessário um link de convite de um usuário ativo da comunidade."
        onClose={() => navigate('/login')}
        confirmButtonText="Fechar"
      />

      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          inviteeName={inviteeName}
        />
      )}
    </OnboardingLayout>
  );
};
